import { z } from 'zod';
import type { ContactDto, PhaseDto, UpdatePhaseCommand } from '@/api/generated';
import { useMailPhaseContacts, useUpdatePhase } from '@/api/hooks';
import { Avatar } from '@/design-system/primitives/Avatar/Avatar';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/design-system/primitives/Dialog/Dialog';
import { Form, FormField, FormSubmitButton } from '@/design-system/primitives/Form/Form';
import { Input } from '@/design-system/primitives/Input/Input';
import { hueForName } from '@/lib/format';
import styles from './PhaseDialogs.module.css';

const STATUS_OPTIONS: Array<{ id: 'Idle' | 'Doing' | 'Done' | 'Late'; label: string }> = [
  { id: 'Idle', label: 'Te starten' },
  { id: 'Doing', label: 'Bezig' },
  { id: 'Done', label: 'Afgerond' },
  { id: 'Late', label: 'Vertraagd' },
];

// progress is stored as a fraction (0–1) on the wire; the dialog asks
// for whole percent so the bouwheer doesn't have to think in decimals.
const schema = z.object({
  status: z.enum(['Idle', 'Doing', 'Done', 'Late']),
  progressPct: z.coerce.number().min(0, 'Min 0%').max(100, 'Max 100%'),
  start: z.string().min(1, 'Kies een startdatum'),
  end: z.string().min(1, 'Kies een einddatum'),
});

type PhaseFormValues = z.infer<typeof schema>;

export function PhaseEditDialog({
  open,
  onOpenChange,
  phase,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  phase: PhaseDto;
}) {
  const updateMutation = useUpdatePhase(phase.id);
  const currentPct =
    phase.progress === null || phase.progress === undefined
      ? 0
      : Math.round(Number(phase.progress) * 100);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Fase bewerken — {phase.label}</DialogTitle>
        <DialogDescription>Pas status, voortgang of de start-/einddatum aan.</DialogDescription>
        <DialogBody>
          <Form<PhaseFormValues>
            schema={schema}
            defaultValues={{
              status: phase.status,
              progressPct: currentPct,
              start: (phase.start ?? '').slice(0, 10),
              end: (phase.end ?? '').slice(0, 10),
            }}
            onSubmit={async (values) => {
              const body: UpdatePhaseCommand = {
                status: values.status,
                // Wire format expects 0–1 fractions.
                progress: (values.progressPct / 100).toString(),
                start: values.start || null,
                end: values.end || null,
              };
              await updateMutation.mutateAsync(body);
              onOpenChange(false);
            }}
          >
            <FormField<PhaseFormValues> name="status" label="Status" required>
              {(field) => (
                <select
                  {...field}
                  className={styles.select}
                  value={(field.value as string) ?? ''}
                  onChange={(e) => field.onChange(e.target.value)}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
              )}
            </FormField>
            <FormField<PhaseFormValues> name="progressPct" label="Voortgang (%)" required>
              {(field) => (
                <Input
                  {...field}
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={
                    field.value === undefined || field.value === null ? '' : String(field.value)
                  }
                  onChange={(e) => field.onChange(e.target.value)}
                />
              )}
            </FormField>
            <FormField<PhaseFormValues> name="start" label="Startdatum" required>
              {(field) => (
                <Input
                  {...field}
                  type="date"
                  value={(field.value as string) ?? ''}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              )}
            </FormField>
            <FormField<PhaseFormValues> name="end" label="Einddatum" required>
              {(field) => (
                <Input
                  {...field}
                  type="date"
                  value={(field.value as string) ?? ''}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              )}
            </FormField>
            <DialogFooter>
              <DialogClose asChild>
                <Btn type="button">Annuleer</Btn>
              </DialogClose>
              <FormSubmitButton>Opslaan</FormSubmitButton>
            </DialogFooter>
          </Form>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Confirm-and-send dialog for "Mail verantwoordelijken". The backend
 * resolves the recipient list and dispatches via Resend; we just preview
 * the local view of who's assigned to this phase so the bouwheer knows
 * roughly what's about to be sent.
 */
export function MailPhaseContactsDialog({
  open,
  onOpenChange,
  phase,
  responsibles,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  phase: PhaseDto;
  responsibles: ContactDto[];
}) {
  const mailMutation = useMailPhaseContacts();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Verantwoordelijken mailen — {phase.label}</DialogTitle>
        <DialogDescription>
          Er wordt een notificatie verstuurd naar elke partij die aan deze fase is toegewezen. De
          exacte ontvangerslijst wordt server-side bepaald op het moment van versturen.
        </DialogDescription>
        <DialogBody>
          {responsibles.length === 0 ? (
            <div className="mono c-ink-3">
              Geen lokaal-bekende verantwoordelijken voor deze fase. De backend zal alsnog de juiste
              contacten resolven.
            </div>
          ) : (
            <ul className={styles.contactList}>
              {responsibles.map((c) => (
                <li key={c.id} className={`row gap-10 ${styles.contactRow}`}>
                  <Avatar name={c.name} size={28} hue={hueForName(c.name)} />
                  <div className="flex-1">
                    <div className={styles.contactName}>{c.name}</div>
                    <div className={styles.contactMeta}>{c.company ?? c.role}</div>
                  </div>
                  <span className={`mono ${styles.contactEmail}`}>{c.email ?? '—'}</span>
                </li>
              ))}
            </ul>
          )}
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Btn type="button" disabled={mailMutation.isPending}>
              Annuleer
            </Btn>
          </DialogClose>
          <Btn
            type="button"
            variant="primary"
            onClick={async () => {
              try {
                await mailMutation.mutateAsync(phase.id);
                onOpenChange(false);
              } catch {
                // toast surfaced by the hook
              }
            }}
            disabled={mailMutation.isPending}
          >
            {mailMutation.isPending ? 'Bezig…' : 'Verstuur'}
          </Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
